import React, { FC } from 'react';
import { Button, Popover } from 'antd';
import {
	CHECKLIST_RESULT_STATUSES,
	ChecklistEvaluation,
	ChecklistExtractionItem,
} from '../../types/ComplianceChecklistTypes';
import {
	CHECKLIST_RESULT_STATUS_COLORS,
	CHECKLIST_RESULT_STATUS_DISPLAY_NAMES,
	EVALUATION_OVERALL_STATUS_COLORS,
	EVALUATION_OVERALL_STATUS_DISPLAY_NAMES,
} from './ComplianceConstants';
import { ComplianceEvaluationStatusDisplay } from './ComplianceEvaluationStatusDisplay';
import {
	EnrichedEvaluationResult,
	buildSkippedResultRows,
	deriveOverallEvaluationStatus,
	describeDocumentIssue,
	describeFoundValue,
	groupEvaluationResultsBySection,
	joinResultsToChecklistItems,
	resultNeedsHumanReview,
	summarizeEvaluationResults,
} from '../../utils/ComplianceEvaluationUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';

// A table cell has room for a verdict, not for fifty requirements. The popover
// lists what needs attention and stops; the full findings stay one click away.
const POPOVER_ITEM_LIMIT = 8;

interface ComplianceEvaluationSummaryProps {
	evaluation: ChecklistEvaluation;
	// Items from the request's stored checklistExtraction — the source of each
	// result's label, section and severity.
	checklistItems: ChecklistExtractionItem[];
	// Opens the full findings. Omit to render the popover without its footer.
	onViewDetails?: () => void;
}

// One-line verdict for a table row ("Does not meet requirements · 47/50"), with
// the drill-down on hover. Shared by the buyer Overview, the buyer reviewer modal
// and the supplier Requests list, so all three read one stored evaluation the
// same way.
export const ComplianceEvaluationSummary: FC<ComplianceEvaluationSummaryProps> = ({
	evaluation,
	checklistItems,
	onViewDetails,
}): React.ReactElement => {
	if (!evaluation) {
		return null;
	}
	const overallStatus = deriveOverallEvaluationStatus(evaluation, checklistItems);
	const counts = summarizeEvaluationResults(evaluation, checklistItems);
	// Null when there is no issue; an issue we do not recognise still gets a line.
	const documentIssueMessage = describeDocumentIssue(nullSafeGet('document_issue', evaluation));

	// Only what needs attention, in the same order the full view uses: failures
	// before items under review, REQUIRED before RECOMMENDED. Items the model
	// skipped are folded in for the same reason they are counted above — "3 to
	// check" over a list naming two of them is worse than no list.
	const needsAttention = groupEvaluationResultsBySection(
		joinResultsToChecklistItems(evaluation, checklistItems).concat(
			buildSkippedResultRows(evaluation, checklistItems)
		)
	).reduce(
		(collected: EnrichedEvaluationResult[], section) =>
			collected.concat(
				section.results.filter((result) => result.status !== CHECKLIST_RESULT_STATUSES.SATISFIED)
			),
		[]
	);
	const shownItems = needsAttention.slice(0, POPOVER_ITEM_LIMIT);
	const hiddenCount = needsAttention.length - shownItems.length;

	const renderPopoverItem = (result: EnrichedEvaluationResult) => {
		const displayStatus = resultNeedsHumanReview(result.status)
			? CHECKLIST_RESULT_STATUSES.NEEDS_HUMAN_REVIEW
			: result.status;
		// A skipped item has no model output to quote, so it says what happened
		// instead of reporting the document as blank on a value nobody looked for.
		const foundClause = result.skipped
			? ' — not checked on your document'
			: ` — found: ${describeFoundValue(result)}`;
		return (
			<div key={result.item_key} className="flex flex-col">
				<span className="bodyregular text-character-primary">{result.label}</span>
				<span className="footnoteregular text-character-secondary">
					<span style={{ color: CHECKLIST_RESULT_STATUS_COLORS[displayStatus] }}>
						{CHECKLIST_RESULT_STATUS_DISPLAY_NAMES[displayStatus]}
					</span>
					{foundClause}
				</span>
			</div>
		);
	};

	// One line of numbers, phrased for the common case: when nothing failed, the
	// breakdown is just noise next to the verdict in the title.
	const countsLabel =
		counts.notMet === 0 && counts.needsReview === 0
			? `All ${counts.total} requirements met`
			: [
					`${counts.met} met`,
					counts.notMet > 0 ? `${counts.notMet} not met` : null,
					counts.needsReview > 0 ? `${counts.needsReview} to check` : null,
			  ]
					.filter((part) => !!part)
					.join(' · ');

	const summaryText = nullSafeGet('summary_for_supplier', evaluation);

	const popoverContent = (
		<div className="flex max-h-80 w-96 flex-col gap-2 overflow-y-auto">
			{counts.total > 0 ? (
				<span className="footnoteregular text-character-secondary">{countsLabel}</span>
			) : null}
			{documentIssueMessage ? (
				<span className="bodyregular text-character-primary">{documentIssueMessage}</span>
			) : null}
			{summaryText ? (
				<span className="bodyregular text-character-primary">{summaryText}</span>
			) : null}
			{shownItems.length > 0 ? (
				<div className="mt-1 flex flex-col gap-2 border-0 border-t border-solid border-grey-4 pt-2">
					{shownItems.map(renderPopoverItem)}
					{hiddenCount > 0 ? (
						<span className="footnoteregular text-character-secondary">and {hiddenCount} more</span>
					) : null}
				</div>
			) : null}
			{onViewDetails ? (
				<Button
					type="primary"
					ghost={true}
					size="small"
					className="mt-1 self-start"
					onClick={onViewDetails}
				>
					View full check
				</Button>
			) : null}
		</div>
	);

	return (
		<Popover
			content={popoverContent}
			title={<ComplianceEvaluationStatusDisplay status={overallStatus} />}
			placement="bottomLeft"
			trigger={['hover', 'click']}
		>
			<span className="footnoteregular cursor-pointer hover:underline">
				<span style={{ color: EVALUATION_OVERALL_STATUS_COLORS[overallStatus] }}>
					{EVALUATION_OVERALL_STATUS_DISPLAY_NAMES[overallStatus]}
				</span>
				{counts.total > 0 ? (
					<span className="text-character-secondary">
						{' '}
						· {counts.met}/{counts.total}
					</span>
				) : null}
			</span>
		</Popover>
	);
};
